import React from "react";
import clsx from "clsx";
import { Button, IconButton, useTheme } from "@material-ui/core";
import {
  AccessTime,
  Block,
  DoneAll,
  ExpandMore,
  GetApp,
  Reply,
  CheckCircleOutline,
  CheckCircle,
} from "@material-ui/icons";
import MarkdownWrapper from "../../MarkdownWrapper";
import ModalImageCors from "../../ModalImageCors";
import LocationPreview from "../../LocationPreview";
import VCardPreview from "../../VCardPreview";
import LinkPreviewCard from "../../LinkPreviewCard";
import useLinkPreview from "../../../hooks/useLinkPreview";
import SelectMessageCheckbox from "../SelectMessageCheckbox";
import WabaTemplateThreadView from "./WabaTemplateThreadView";

const formatFileSize = (bytes) => {
  if (bytes === 0) return '0 B';
  const k = 1024;
  const sizes = ['B', 'kB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
};

const normalizeMediaUrl = (mediaUrl) => {
  if (!mediaUrl) return "";

  const origin = window.location.origin.replace(/\/$/, "");
  let normalizedUrl = mediaUrl.trim();

  if (normalizedUrl.startsWith("http://") || normalizedUrl.startsWith("https://") || normalizedUrl.startsWith("blob:")) {
    normalizedUrl = normalizedUrl.replace(/\/public\/\/public\//g, '/public/');
    normalizedUrl = normalizedUrl.replace(/\/public\/public\//g, '/public/');
    return normalizedUrl;
  }

  if (normalizedUrl.startsWith("/public/")) {
    normalizedUrl = normalizedUrl.replace(/^\/public\/\/public\//, '/public/');
    normalizedUrl = normalizedUrl.replace(/^\/public\/public\//, '/public/');
    return `${origin}${normalizedUrl}`;
  }

  let normalizedPath = normalizedUrl.replace(/^\/+/, "");
  normalizedPath = normalizedPath.replace(/^public\//, "");
  return `${origin}/public/${normalizedPath}`;
};

const MessageItem = ({
  message,
  index,
  filteredMessages,
  lastMessageRef,
  classes,
  isGroup,
  readOnlyPreview,
  showSelectMessageCheckbox,
  formatTime,
  hanldeReplyMessage,
  handleOpenMessageOptionsMenu,
  getSenderColor,
  formatSenderDisplay,
}) => {
  const theme = useTheme();

  // Fetch og: preview for plain-text messages that don't already carry linkPreview from backend
  const isPlainText = !message.mediaUrl && (
    !message.mediaType || message.mediaType === "conversation" || message.mediaType === "extendedTextMessage"
  );
  const fetchedPreview = useLinkPreview(isPlainText && !message.linkPreview ? message.body : null);
  const effectivePreview = message.linkPreview || fetchedPreview;

  const checkMessageMedia = (message) => {
    if (message.mediaType === "locationMessage" && message.body?.split('|').length >= 2) {
      const parts = message.body.split('|');
      let lat, lng, thumbnail = null;

      if (parts[0].startsWith('data:')) {
        // Formato viejo (mensajes en DB anteriores al fix): data:...|lat|lng
        lat = parts[1];
        lng = parts[2];
      } else {
        // Formato nuevo: lat|lng  o  lat|lng|base64puro
        lat = parts[0];
        lng = parts[1];
        if (parts[2]) thumbnail = `data:image/jpeg;base64,${parts[2]}`;
      }

      const mapsUrl = `https://maps.google.com/?q=${lat},${lng}`;
      return <LocationPreview lat={lat} lng={lng} thumbnail={thumbnail} mapsUrl={mapsUrl} />;
    }
    else
    if (message.mediaType === "contactMessage") {
      return <VCardPreview message={message} />;
    }
    else if (message.mediaType === "image") {
      return <ModalImageCors imageUrl={message.mediaUrl} message={message} />;
    } else if (message.mediaType === "audio") {
      const normalizedAudioUrl = normalizeMediaUrl(message.mediaUrl);
      return (
        <audio controls>
          <source src={normalizedAudioUrl} type="audio/mp3"></source>
        </audio>
      );
    } else if (message.mediaType === "video") {
      const normalizedVideoUrl = normalizeMediaUrl(message.mediaUrl);
      return (
        <video
          className={classes.messageMedia}
          src={normalizedVideoUrl}
          controls
        />
      );
    } else {
      return (
        <>
          <div className={classes.downloadMedia}>
            <div style={{
              display: 'flex',
              alignItems: 'center',
              padding: '12px',
              backgroundColor: theme.mode === 'dark' ? '#374151' : '#f8f9fa',
              borderRadius: '8px',
              border: `1px solid ${theme.mode === 'dark' ? '#4B5563' : '#e9ecef'}`,
              marginBottom: '8px',
              maxWidth: '300px'
            }}>
              {/* Icono del archivo */}
              {(() => {
                let fileName = message.body || 'document';

                if (message.body && message.body.includes('.')) {
                  fileName = message.body;
                } else if (message.mediaUrl) {
                  const urlFileName = message.mediaUrl.split('/').pop();
                  if (urlFileName && urlFileName.includes('.')) {
                    const parts = urlFileName.split('.');
                    if (parts.length > 2 && /^\d+$/.test(parts[0])) {
                      fileName = parts.slice(1).join('.');
                    } else {
                      fileName = urlFileName;
                    }
                  }
                }

                const extension = fileName.split('.').pop()?.toLowerCase();

                let iconConfig;
                if (['doc', 'docx'].includes(extension)) {
                  iconConfig = { bg: '#2b579a', text: 'W' };
                } else if (['xls', 'xlsx', 'csv'].includes(extension)) {
                  iconConfig = { bg: '#217346', text: '📊' };
                } else if (['pdf'].includes(extension)) {
                  iconConfig = { bg: '#dc3545', text: 'PDF' };
                } else if (['ppt', 'pptx'].includes(extension)) {
                  iconConfig = { bg: '#d24726', text: 'P' };
                } else if (['txt'].includes(extension)) {
                  iconConfig = { bg: '#6c757d', text: 'T' };
                } else {
                  iconConfig = { bg: '#6c757d', text: '📄' };
                }

                return (
                  <div style={{
                    width: '40px',
                    height: '40px',
                    borderRadius: '6px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    marginRight: '12px',
                    fontSize: '16px',
                    fontWeight: 'bold',
                    color: 'white',
                    backgroundColor: iconConfig.bg
                  }}>
                    {iconConfig.text}
                  </div>
                );
              })()}

              {/* Información del archivo */}
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{
                  fontSize: '14px',
                  fontWeight: '600',
                  color: theme.mode === 'dark' ? '#f9fafb' : '#212529',
                  marginBottom: '2px',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap'
                }}>
                  {(() => {
                    let fileName = message.body || 'Documento';

                    if (message.body && message.body.includes('.')) {
                      fileName = message.body;
                    } else if (message.mediaUrl) {
                      const urlFileName = message.mediaUrl.split('/').pop();
                      if (urlFileName && urlFileName.includes('.')) {
                        const parts = urlFileName.split('.');
                        if (parts.length > 2 && /^\d+$/.test(parts[0])) {
                          fileName = parts.slice(1).join('.');
                        } else {
                          fileName = urlFileName;
                        }
                      }
                    }

                    return fileName;
                  })()}
                </div>
                <div style={{
                  fontSize: '12px',
                  color: theme.mode === 'dark' ? '#9ca3af' : '#6c757d',
                  display: 'flex',
                  alignItems: 'center'
                }}>
                  {(() => {
                    let fileName = message.body || 'document';

                    if (message.body && message.body.includes('.')) {
                      fileName = message.body;
                    } else if (message.mediaUrl) {
                      const urlFileName = message.mediaUrl.split('/').pop();
                      if (urlFileName && urlFileName.includes('.')) {
                        const parts = urlFileName.split('.');
                        if (parts.length > 2 && /^\d+$/.test(parts[0])) {
                          fileName = parts.slice(1).join('.');
                        } else {
                          fileName = urlFileName;
                        }
                      }
                    }

                    const extension = fileName.split('.').pop()?.toUpperCase() || 'DOC';
                    const fileSize = message.mediaSize || 0;

                    return `${extension} • ${formatFileSize(fileSize)}`;
                  })()}
                </div>
              </div>

              {/* Botón de descarga minimalista */}
              <Button
                size="small"
                style={{
                  minWidth: '32px',
                  width: '32px',
                  height: '32px',
                  borderRadius: '50%',
                  backgroundColor: theme.mode === 'dark' ? '#374151' : '#f8f9fa',
                  border: `1px solid ${theme.mode === 'dark' ? '#4B5563' : '#dee2e6'}`,
                  color: theme.mode === 'dark' ? '#9ca3af' : '#6c757d',
                  padding: 0
                }}
                target="_blank"
                href={normalizeMediaUrl(message.mediaUrl)}
              >
                <GetApp style={{ fontSize: '16px' }} />
              </Button>
            </div>
          </div>
        </>
      );
    }
  };

  const renderMessageAck = (message) => {
    if (!message.fromMe) {
      return null;
    }

    if (message.ack === 0) {
      return <AccessTime fontSize="small" className={classes.ackIcons} />;
    }
    if (message.ack === 1) {
      return <CheckCircleOutline fontSize="small" className={classes.ackIcons} />;
    }
    if (message.ack === 2) {
      return <CheckCircle fontSize="small" className={classes.ackIcons} />;
    }
    if (message.ack === 3) {
      return <DoneAll fontSize="small" className={classes.ackIcons} />;
    }
    if (message.ack === 4) {
      return <DoneAll fontSize="small" className={classes.ackIcons} color="primary" />;
    }
    return <AccessTime fontSize="small" className={classes.ackIcons} />;
  };

  const renderQuotedMessage = (message) => {
    return (
      <div
        className={clsx(classes.quotedContainerLeft, {
          [classes.quotedContainerRight]: message.fromMe,
        })}
      >
        <span
          className={clsx(classes.quotedSideColorLeft, {
            [classes.quotedSideColorRight]: message.quotedMsg?.fromMe,
          })}
        ></span>
        <div className={classes.quotedMsg}>
          {!message.quotedMsg?.fromMe && (
            <span
              className={classes.messageContactName}
              style={{
                color: getSenderColor(
                  message.quotedMsg?.senderContact?.number ||
                  message.quotedMsg?.senderContact?.id ||
                  message.quotedMsg?.contact?.number ||
                  message.quotedMsg?.participant
                )
              }}
            >
              {formatSenderDisplay(message.quotedMsg?.senderContact, message.quotedMsg?.contact, message.quotedMsg?.participant)}
            </span>
          )}

          {message.quotedMsg.mediaType === "audio"
            && (
              <div className={classes.downloadMedia}>
                <audio controls>
                  <source src={normalizeMediaUrl(message.quotedMsg.mediaUrl)} type="audio/mp3"></source>
                </audio>
              </div>
            )
          }
          {message.quotedMsg.mediaType === "video"
            && (
              <video
                className={classes.messageMedia}
                src={normalizeMediaUrl(message.quotedMsg.mediaUrl)}
                controls
              />
            )
          }
          {message.quotedMsg.mediaType === "application"
            && (
              <div className={classes.downloadMedia}>
                <Button
                  startIcon={<GetApp />}
                  color="primary"
                  variant="outlined"
                  target="_blank"
                  href={normalizeMediaUrl(message.quotedMsg.mediaUrl)}
                >
                  Download
                </Button>
              </div>
            )
          }

          {message.quotedMsg.mediaType === "image" ? (
            <ModalImageCors imageUrl={message.quotedMsg.mediaUrl} />
          ) : (
            message.quotedMsg?.body
          )}

        </div>
      </div>
    );
  };

  const renderMessageReactions = (message) => {
    if (!message.reactions || message.reactions.length === 0) {
      return null;
    }

    const lastReaction = message.reactions[message.reactions.length - 1];

    return (
      <div style={{
        position: 'absolute',
        bottom: '-30px',
        right: '8px',
        display: 'flex',
        alignItems: 'center',
        backgroundColor: 'rgba(255, 255, 255, 0.95)',
        borderRadius: '16px',
        padding: '4px 8px',
        boxShadow: '0 2px 4px rgba(0, 0, 0, 0.15)',
        zIndex: 5,
        fontSize: '16px',
        minWidth: '32px',
        justifyContent: 'center'
      }}>
        <span>{lastReaction.type}</span>
      </div>
    );
  };

  if (message.mediaType === "delivery_warning") {
    return (
      <div style={{ display: "flex", justifyContent: "center", padding: "4px 16px", margin: "2px 0" }}>
        <div style={{
          display: "flex",
          alignItems: "flex-start",
          gap: "8px",
          background: theme.mode === "dark" ? "#451a03" : "#fff7ed",
          border: `1px solid ${theme.mode === "dark" ? "#92400e" : "#fed7aa"}`,
          borderRadius: "8px",
          padding: "10px 14px",
          maxWidth: "520px",
          width: "100%",
        }}>
          <span style={{ fontSize: "15px", flexShrink: 0, marginTop: "1px" }}>⚠️</span>
          <div>
            <span style={{
              fontWeight: 700,
              fontSize: "12px",
              color: theme.mode === "dark" ? "#fb923c" : "#9a3412",
              display: "block",
              marginBottom: "3px",
            }}>
              Posible falla de entrega
            </span>
            <span style={{
              fontSize: "12px",
              color: theme.mode === "dark" ? "#fdba74" : "#7c2d12",
              lineHeight: "1.4",
            }}>
              {message.body}
            </span>
          </div>
        </div>
      </div>
    );
  }

  if (!message.fromMe) {
    return (
      <div
        className={classes.messageLeft}
        title={message.queueId && message.queue?.name}
        onDoubleClick={readOnlyPreview ? undefined : (e) => hanldeReplyMessage(e, message)}
        style={{ position: 'relative' }}
        ref={index === filteredMessages.length - 1 ? lastMessageRef : null}
      >
        {showSelectMessageCheckbox && !readOnlyPreview && (
          <SelectMessageCheckbox
            message={message}
          />
        )}
        {message.isForwarded && (
          <div>
            <span className={classes.forwardMessage}
            ><Reply style={{ color: "grey", transform: 'scaleX(-1)' }} /> Encaminhada
            </span>
            <br />
          </div>
        )}
        {isGroup && !message.fromMe && (
          <span
            className={classes.messageContactName}
            style={{
              color: getSenderColor(
                message.senderContact?.number ||
                message.senderContact?.id ||
                message.contact?.number ||
                message.participant
              )
            }}
          >
            {formatSenderDisplay(message.senderContact, message.contact, message.participant)}
          </span>
        )}

        {/* aviso de mensagem apagado pelo contato */}
        {message.isDeleted && (
          <div>
            <span className={"message-deleted"}
            >Essa mensagem foi apagada pelo contato &nbsp;
              <Block
                color="error"
                fontSize="small"
                className={classes.deletedIcon}
              />
            </span>
          </div>
        )}

        {(message.mediaUrl || message.mediaType === "locationMessage" || message.mediaType === "vcard" || message.mediaType === "contactMessage"
        ) && checkMessageMedia(message)}
        <div className={classes.textContentItem}>
          {message.quotedMsg && renderQuotedMessage(message)}
          {message.mediaType !== "reactionMessage" && (
            <MarkdownWrapper message={message}>
              {message.mediaType === "locationMessage" || message.mediaType === "contactMessage"
                ? null
                : message.body}
            </MarkdownWrapper>
          )}
          {effectivePreview && <LinkPreviewCard preview={effectivePreview} />}

          <span className={classes.timestamp}>
            {" "}{formatTime(message.createdAt || message.timestamp || new Date())}
            {renderMessageAck(message)}
          </span>
          {/* Botón de opciones (oculto en vista previa / espiar) */}
          {!readOnlyPreview && (
            <IconButton
              size="small"
              id="messageActionsButton"
              disabled={message.isDeleted}
              onClick={(e) => handleOpenMessageOptionsMenu(e, message)}
              style={{
                position: 'absolute',
                top: '0px',
                right: '8px',
                padding: "4px",
                color: "#ffffff",
                fontSize: "14px",
                backgroundColor: "rgba(0, 0, 0, 0.3)",
                borderRadius: "50%",
                width: "24px",
                height: "24px",
                zIndex: 10,
                "&:hover": {
                  backgroundColor: "rgba(0, 0, 0, 0.5)"
                }
              }}
            >
              <ExpandMore style={{ fontSize: "14px" }} />
            </IconButton>
          )}
        </div>
        {renderMessageReactions(message)}
      </div>
    );
  }

  return (
    <div
      className={classes.messageRight}
      onDoubleClick={readOnlyPreview ? undefined : (e) => hanldeReplyMessage(e, message)}
      style={{ position: 'relative' }}
      ref={index === filteredMessages.length - 1 ? lastMessageRef : null}
    >
      {showSelectMessageCheckbox && !readOnlyPreview && (
        <SelectMessageCheckbox
          message={message}
        />
      )}
      {message.isForwarded && (
        <div>
          <span className={classes.forwardMessage}
          ><Reply style={{ color: "grey", transform: 'scaleX(-1)' }} /> Encaminhada
          </span>
          <br />
        </div>
      )}
      {(message.mediaUrl || message.mediaType === "locationMessage" || message.mediaType === "vcard" || message.mediaType === "contactMessage"
      ) && checkMessageMedia(message)}
      <div
        className={clsx(classes.textContentItem, {
          [classes.textContentItemDeleted]: message.isDeleted,
        })}
      >
        {message.isDeleted && (
          <Block
            color="disabled"
            fontSize="small"
            className={classes.deletedIcon}
          />
        )}
        {message.quotedMsg && renderQuotedMessage(message)}
        {/* 0367 (H9): plantilla HSM (Meta) → render dedicado (header/body/footer/botones). */}
        {message.mediaType === "template" && <WabaTemplateThreadView message={message} />}
        {/* Mostrar el body para todos los tipos de mensaje excepto ubicaciones, vcards y plantillas */}
        {message.mediaType !== "locationMessage" &&
         message.mediaType !== "contactMessage" &&
         message.mediaType !== "template" &&
         message.body && (
          <MarkdownWrapper message={message}>{message.body}</MarkdownWrapper>
        )}
        {effectivePreview && <LinkPreviewCard preview={effectivePreview} />}

        <span className={classes.timestamp}>
          {" "}{formatTime(message.createdAt || message.timestamp || new Date())}
          {renderMessageAck(message)}
        </span>
        {/* Botón de opciones (oculto en vista previa / espiar) */}
        {!readOnlyPreview && (
          <IconButton
            size="small"
            id="messageActionsButton"
            disabled={message.isDeleted}
            onClick={(e) => handleOpenMessageOptionsMenu(e, message)}
            style={{
              position: 'absolute',
              top: '0px',
              right: '8px',
              padding: "4px",
              color: "#ffffff",
              fontSize: "14px",
              backgroundColor: "rgba(0, 0, 0, 0.3)",
              borderRadius: "50%",
              width: "24px",
              height: "24px",
              zIndex: 10,
              "&:hover": {
                backgroundColor: "rgba(0, 0, 0, 0.5)"
              }
            }}
          >
            <ExpandMore style={{ fontSize: "14px" }} />
          </IconButton>
        )}
      </div>
      {renderMessageReactions(message)}
    </div>
  );
};

export default MessageItem;
